/**
 * FH Brand Highlights
 * Custom Element for brand showcase with slider functionality
 * OOP pattern following FHTestimonialSlider
 */

if (!customElements.get('fh-brand-highlights')) {
  class FHBrandHighlights extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.sliderThreshold = 3;
      this.brandCount = 0;
      this.isSliderMode = false;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');

      if (!this.track) return;

      // Get configuration from data attributes
      this.sliderThreshold = parseInt(this.dataset.sliderThreshold, 10) || 3;
      this.brandCount = parseInt(this.dataset.brandCount, 10) || 0;
      
      // Determine if slider mode should be active
      this.checkSliderMode();
      
      // Bind events
      this.bindEvents();
      
      // Initial navigation state
      this.updateNavigation();

      // Re-check on resize (breakpoint changes)
      this.resizeObserver = new ResizeObserver(() => {
        this.checkSliderMode();
        this.updateNavigation();
      });
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    /**
     * Check if slider mode should be active based on viewport and brand count
     */
    checkSliderMode() {
      const viewportWidth = window.innerWidth;
      
      // Tablet and mobile: always slider mode if more than 1 brand
      if (viewportWidth <= 1024) {
        this.isSliderMode = this.brandCount > 1;
      } else {
        // Desktop: slider mode if brands exceed threshold
        this.isSliderMode = this.brandCount > this.sliderThreshold;
      }

      // Toggle track grid mode
      if (this.track) {
        this.track.classList.toggle('fh-bh__track--grid', !this.isSliderMode && viewportWidth > 1024);
      }

      // Toggle navigation visibility
      this.toggleNavigation();
    }

    /**
     * Show/hide navigation buttons based on slider mode
     */
    toggleNavigation() {
      const navContainers = this.querySelectorAll('.fh-bh__nav');
      navContainers.forEach(nav => {
        nav.classList.toggle('fh-bh__nav--hidden', !this.isSliderMode);
      });
    }

    bindEvents() {
      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollPrev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollNext());
      }

      // Update navigation state on scroll
      this.track.addEventListener('scroll', () => {
        this.updateNavigation();
      });
    }

    /**
     * Calculate scroll amount based on card width and gap
     */
    getCardWidth() {
      const card = this.track.querySelector('.fh-bh__item');
      if (!card) return 300;
      
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.gap) || 0;
      return card.offsetWidth + gap;
    }

    scrollPrev() {
      if (!this.isSliderMode) return;
      
      const scrollAmount = this.getCardWidth();
      this.track.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }

    scrollNext() {
      if (!this.isSliderMode) return;
      
      const scrollAmount = this.getCardWidth();
      this.track.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }

    /**
     * Update navigation button states (disabled at start/end)
     */
    updateNavigation() {
      if (!this.prevBtn || !this.nextBtn || !this.isSliderMode) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const isAtStart = scrollLeft <= 1;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      this.prevBtn.disabled = isAtStart;
      this.nextBtn.disabled = isAtEnd;
    }
  }

  customElements.define('fh-brand-highlights', FHBrandHighlights);
}
